import { colors } from '@kmns-internal/furikake/tokens'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  type SxProps,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'

interface ContainerProps {
  children?: React.ReactNode
  sx?: SxProps
  style?: React.CSSProperties
  className?: string
  'data-testid'?: string
}

export const Container: React.FC<ContainerProps> = ({
  children,
  sx,
  ...props
}) => (
  <Box
    sx={{
      width: '100%',
      borderTop: 'none',
      display: 'flex',
      alignItems: 'center',
      p: 0.5,
      flexWrap: 'wrap',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
)

export const OptionWrapper: React.FC<{
  children?: React.ReactNode
  style?: React.CSSProperties
}> = ({ children, style }) => (
  <Typography component="div" variant="body2" sx={{ ml: 1 }} style={style}>
    {children}
  </Typography>
)

export const OptionInformationImagesWrapper: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => <Box sx={{ width: '100%' }}>{children}</Box>

export const OptionColWrapper: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => (
  <Stack direction="row" alignItems="center">
    {children}
  </Stack>
)

export const RuleLabel: React.FC<{
  children?: React.ReactNode
  id?: string
  style?: React.CSSProperties
}> = ({ children, id, style }) => (
  <Typography
    variant="body2"
    component="label"
    sx={{ margin: 'auto' }}
    id={id}
    style={style}
  >
    {children}
  </Typography>
)

export const FormatLabel: React.FC<{
  children?: React.ReactNode
  id?: string
}> = ({ children, id }) => (
  <Typography variant="body2" component="label" sx={{ mr: 0.5 }} id={id}>
    {children}
  </Typography>
)

export const InformationError: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="body2"
    component="p"
    sx={{ m: 0, ml: 1, color: colors.error.main }}
  >
    {children}
  </Typography>
)

export const InformationImagesError: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => (
  <Typography
    variant="body2"
    component="p"
    sx={{ mt: 0, ml: 0, mr: 0, mb: 1, color: colors.error.main }}
  >
    {children}
  </Typography>
)

export const ScaleLabel: React.FC<{
  children?: React.ReactNode
  id?: string
}> = ({ children, id }) => (
  <Typography
    variant="body2"
    component="label"
    sx={{ ml: 1, mr: 1, alignSelf: 'center' }}
    id={id}
  >
    {children}
  </Typography>
)

interface ScaleInputProps {
  name?: string
  placeholder?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  'aria-labelledby'?: string
}

export const ScaleInput: React.FC<ScaleInputProps> = ({
  'aria-labelledby': ariaLabelledby,
  ...props
}) => (
  <TextField
    size="small"
    inputProps={{ 'aria-labelledby': ariaLabelledby }}
    sx={{ width: 90 }}
    {...props}
  />
)

export const DatetimeRuleContainer: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => <Box sx={{ ml: 3, mt: 0.5 }}>{children}</Box>

interface DatetimeRuleInputProps {
  min?: number
  max?: number
  value?: number
  onChange?: (value: number | string | null) => void
  'aria-labelledby'?: string
}

export const DatetimeRuleInput: React.FC<DatetimeRuleInputProps> = ({
  min = 0,
  max,
  value,
  onChange,
  'aria-labelledby': ariaLabelledby,
}) => (
  <TextField
    type="number"
    size="small"
    inputProps={{ min, max, 'aria-labelledby': ariaLabelledby }}
    value={value ?? ''}
    onChange={e =>
      onChange?.(e.target.value === '' ? null : Number(e.target.value))
    }
    sx={{ width: 64 }}
  />
)

export const DatetimeRuleDayText: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => (
  <Typography component="span" sx={{ display: 'inline-block', ml: 0.5 }}>
    {children}
  </Typography>
)

export const DatetimeRuleDescription: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => (
  <Typography
    sx={{
      color: colors.text.secondary,
    }}
    variant="caption"
  >
    {children}
  </Typography>
)

export const InformationImagesText: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => (
  <Typography variant="body2" component="span" sx={{}}>
    {children}
  </Typography>
)

export const InformationImagesWrapper: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => <Box sx={{ ml: 1, mt: 1 }}>{children}</Box>

interface NoPaddingCheckboxProps {
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  children?: React.ReactNode
}

export const NoPaddingCheckbox: React.FC<NoPaddingCheckboxProps> = ({
  checked,
  onChange,
  children,
}) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={onChange} />}
    label={children}
    sx={{ '& .MuiFormControlLabel-label': { pr: 0 } }}
  />
)
